
import { supabase } from "../integrations/supabase/client";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import { canAddPet } from "./subscriptionService";
import { createProfilePhotoPost } from "./petProfilePostService";
import { optimizeImage } from "../utils/imageOptimization";

export async function getUserPets() {
  const { data: { user }, error: userError } = await supabase.auth.getUser();
  
  if (userError || !user || !user.id) {
    throw new Error("User not authenticated");
  }

  const { data, error } = await supabase
    .from('pets')
    .select('*')
    .eq('user_id', user.id)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching user pets:', error);
    throw new Error(`Error fetching pets: ${error.message}`);
  }

  return data || [];
}

export async function getPetById(petId: string) {
  if (!petId || petId === 'undefined' || petId === 'null') {
    throw new Error('Pet ID is required');
  }

  try {
    const { data, error } = await supabase
      .from('pets')
      .select('*')
      .eq('id', petId)
      .maybeSingle();

    if (error) {
      throw new Error(`Error fetching pet: ${error.message}`);
    }

    if (!data) {
      throw new Error('Pet not found');
    }

    return data;
  } catch (err) {
    throw err;
  }
}

export async function uploadPetPhoto(rawFile: File, userId: string): Promise<string> {
  try {
    // Optimize image before upload (no-op for non-images / already small files)
    const file = rawFile.type.startsWith('image/')
      ? await optimizeImage(rawFile, { preset: 'feed' })
      : rawFile;
    const fileExt = file.name.split('.').pop();
    const fileName = `${userId}/${uuidv4()}.${fileExt}`;
    const filePath = `${fileName}`;

    console.log("Uploading pet photo to path:", filePath, '(size:', file.size, 'original:', rawFile.size, ')');

    const { error: uploadError, data: uploadData } = await supabase.storage
      .from('pets')
      .upload(filePath, file);

    if (uploadError) {
      console.error("Upload error:", uploadError);
      
      if (uploadError.message.includes('new row violates row-level security policy')) {
        throw new Error(`Storage permission issue: Please make sure you're authenticated and have proper permissions.`);
      }
      
      throw new Error(`Error uploading photo: ${uploadError.message}`);
    }

    const { data } = supabase.storage
      .from('pets')
      .getPublicUrl(filePath);

    console.log('Uploaded pet photo:', data.publicUrl);
    return data.publicUrl;
  } catch (error: any) {
    console.error('Error in uploadPetPhoto:', error);
    throw error;
  }
}

export async function createPet(petData: {
  name: string;
  species?: string;
  breed?: string;
  breed_id?: number; // ✅ NEW: Support for breed_id
  sex?: 'male' | 'female' | 'not_specified';
  birth_date?: string;
  weight_kg?: number;
  color?: string;
  photo?: File;
  photo_url?: string;
  personality_traits?: string[];
  privacy_setting: 'public' | 'private' | 'followers_only';
  is_memorial: boolean;
  death_date?: string | null;
  location?: string;
}) {
  const { data: { user }, error: userError } = await supabase.auth.getUser();
  
  if (userError || !user || !user.id) {
    throw new Error("User not authenticated");
  }

  try {
    console.log('🔍 createPet - Starting pet creation...');
    
    // ✅ Optional frontend validation - database trigger will be the final authority
    const petLimitCheck = await canAddPet();
    console.log('🔍 createPet - Frontend limit check result:', petLimitCheck);
    
    // Validate weight if provided - updated to allow up to 1500 kg
    if (petData.weight_kg && (petData.weight_kg <= 0 || petData.weight_kg > 1500)) {
      throw new Error("Weight must be between 0.01 and 1500 kg");
    }
    
    // Handle photo upload if provided
    let photoUrl = petData.photo_url;
    
    if (petData.photo) {
      console.log('Uploading pet photo:', petData.photo.name);
      photoUrl = await uploadPetPhoto(petData.photo, user.id);
    }

    // ✅ IMPROVED: Support both breed_id and breed for compatibility
    const petCreateData = {
      name: petData.name,
      species: petData.species,
      breed: petData.breed,
      breed_id: petData.breed_id || null, // ✅ NEW: Include breed_id
      sex: petData.sex,
      birth_date: petData.birth_date,
      weight_kg: petData.weight_kg,
      color: petData.color,
      photo_url: photoUrl, // Only send photo_url, not photo
      personality_traits: petData.personality_traits,
      privacy_setting: petData.privacy_setting,
      is_memorial: petData.is_memorial,
      death_date: petData.death_date || null,
      location: petData.location,
      user_id: user.id
    };

    console.log('🔍 createPet - Creating pet with data:', petCreateData);

    // ✅ Create the pet - database trigger will handle the final limit enforcement
    const { data, error } = await supabase
      .from('pets')
      .insert(petCreateData)
      .select()
      .single();

    if (error) {
      console.error('🔍 createPet - Database error:', error);
      
      // ✅ Handle specific database trigger errors
      if (error.message.includes('Pet limit reached') || error.message.includes('limit')) {
        const errorMessage = "Pet limit reached for your subscription tier. Please upgrade to add more pets.";
        toast.error(errorMessage);
        throw new Error(errorMessage);
      }
      
      throw new Error(`Error creating pet: ${error.message}`);
    }

    console.log('🔍 createPet - Pet created successfully:', data);
    
    // ✅ NEW: Auto-create profile photo post if photo was uploaded
    if (photoUrl && data.id) {
      try {
        await createProfilePhotoPost({
          petId: data.id,
          petName: data.name,
          photoUrl,
          isNewPet: true
        });
        console.log('📸 Profile photo post created for new pet');
      } catch (error) {
        console.error('📸 Failed to create profile photo post:', error);
        // Don't fail pet creation if post creation fails
      }
    }
    
    return data;
    
  } catch (error: any) {
    console.error('🔍 createPet - Error in createPet:', error);
    throw error;
  }
}

export async function updatePet(petId: string, petData: Partial<{
  name: string;
  species: string;
  breed: string;
  sex: 'male' | 'female' | 'not_specified';
  birth_date: string;
  weight_kg: number;
  color: string;
  photo: File;
  photo_url: string;
  personality_traits: string[];
  privacy_setting: 'public' | 'private' | 'followers_only';
  is_memorial: boolean;
  death_date: string | null;
  location: string;
}>) {
  const { data: { user }, error: userError } = await supabase.auth.getUser();
  
  if (userError || !user || !user.id) {
    throw new Error("User not authenticated");
  }

  try {
    // ✅ NEW: Get current pet data to check for photo changes
    const { data: currentPet, error: fetchError } = await supabase
      .from('pets')
      .select('photo_url, name')
      .eq('id', petId)
      .eq('user_id', user.id)
      .single();

    if (fetchError) {
      throw new Error(`Error fetching current pet data: ${fetchError.message}`);
    }

    // Handle photo upload if provided
    let updateData = { ...petData };
    let newPhotoUrl = currentPet?.photo_url;
    
    if (petData.photo) {
      newPhotoUrl = await uploadPetPhoto(petData.photo, user.id);
      updateData.photo_url = newPhotoUrl;
      delete updateData.photo;
    }

    const { data, error } = await supabase
      .from('pets')
      .update(updateData)
      .eq('id', petId)
      .eq('user_id', user.id)
      .select()
      .single();

    if (error) {
      throw new Error(`Error updating pet: ${error.message}`);
    }

    // ✅ NEW: Auto-create profile photo post if photo was changed
    if (newPhotoUrl && currentPet && newPhotoUrl !== currentPet.photo_url) {
      try {
        await createProfilePhotoPost({
          petId: data.id,
          petName: data.name,
          photoUrl: newPhotoUrl,
          isNewPet: false
        });
        console.log('📸 Profile photo post created for updated pet');
      } catch (error) {
        console.error('📸 Failed to create profile photo post:', error);
        // Don't fail pet update if post creation fails
      }
    }

    return data;
  } catch (error: any) {
    console.error('Error in updatePet:', error);
    throw error;
  }
}

export async function deletePet(petId: string) {
  console.log('🗑️ deletePet service - Starting deletion for pet:', petId);
  
  const { data: { user }, error: userError } = await supabase.auth.getUser();
  
  if (userError || !user || !user.id) {
    console.error('🗑️ deletePet service - User not authenticated:', userError);
    throw new Error("User not authenticated");
  }

  try {
    // First, let's verify the pet exists and belongs to the user
    const { data: petData, error: fetchError } = await supabase
      .from('pets')
      .select('id, name, user_id')
      .eq('id', petId)
      .eq('user_id', user.id)
      .single();

    if (fetchError) {
      console.error('🗑️ deletePet service - Error fetching pet:', fetchError);
      throw new Error(`Error verifying pet ownership: ${fetchError.message}`);
    }

    if (!petData) {
      console.error('🗑️ deletePet service - Pet not found or not owned by user');
      throw new Error('Pet not found or you do not have permission to delete it');
    }

    console.log('🗑️ deletePet service - Pet found, proceeding with deletion:', petData);

    // Delete the pet - this should cascade to related records due to database constraints
    const { error: deleteError } = await supabase
      .from('pets')
      .delete()
      .eq('id', petId)
      .eq('user_id', user.id);

    if (deleteError) {
      console.error('🗑️ deletePet service - Error deleting pet:', deleteError);
      throw new Error(`Error deleting pet: ${deleteError.message}`);
    }

    console.log('🗑️ deletePet service - Pet deleted successfully:', petId);
    return true;
  } catch (error: any) {
    console.error('🗑️ deletePet service - Delete operation failed:', error);
    throw error;
  }
}
